import { createContext, useContext, useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("da_token"));
  const [client, setClient] = useState(undefined); // undefined = checking session

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — AuthCallback exchanges session_id first, skip the /me check.
    if (window.location.hash?.includes("session_id=")) return;
    if (!token) {
      setClient(null);
      return;
    }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("session invalid");
        return r.json();
      })
      .then(setClient)
      .catch(() => {
        localStorage.removeItem("da_token");
        setToken(null);
        setClient(null);
      });
  }, [token]);

  const applySession = (data) => {
    localStorage.setItem("da_token", data.token);
    setToken(data.token);
    setClient(data.client);
    return data;
  };

  const login = async (email, password) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(typeof d.detail === "string" ? d.detail : "Login failed");
    }
    return applySession(await r.json());
  };

  const googleLogin = async (sessionId) => {
    const r = await fetch(`${API}/auth/google/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(typeof d.detail === "string" ? d.detail : "Google sign-in failed");
    }
    return applySession(await r.json());
  };

  const logout = () => {
    localStorage.removeItem("da_token");
    setToken(null);
    setClient(null);
  };

  return <AuthCtx.Provider value={{ token, client, login, googleLogin, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export async function authFetch(path, options = {}) {
  const token = localStorage.getItem("da_token");
  const r = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (r.status === 401) {
    localStorage.removeItem("da_token");
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  return r;
}
